import type { GroupOption } from '@exam-management/common/types';
import { ExamCourseLanguage, ExamCourseType } from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type SearchSelectionProps,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Form,
  FormRadioSelection,
  FormSearchSelection,
  FormTextField,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useState } from 'react';
import { type SubmitHandler, FormProvider, useForm } from 'react-hook-form';

import {
  ExaminationCourseCreationFormSchema,
  type ExaminationCourseCreationFormInput,
} from '../../schema';

import { transformExamCourseCreationErrors } from './utils';

export interface ExaminationCourseCreationFormProps {
  onSubmit: SubmitHandler<ExaminationCourseCreationFormInput>;
  onClose: () => void;
  slotProps: {
    group: Pick<SearchSelectionProps, 'options' | 'loading' | 'onSearch'>;
  };
}

export default function ExaminationCourseCreationForm(
  props: Readonly<ExaminationCourseCreationFormProps>,
) {
  const { onSubmit, onClose, slotProps } = props;
  const logger = useLogger();
  const { group } = slotProps;

  const [inputValue, setInputValue] = useState<string | null>(null);

  const formContext = useForm<ExaminationCourseCreationFormInput>({
    resolver: zodResolver(ExaminationCourseCreationFormSchema),
    defaultValues: {
      name: '',
      code: '',
      group: null,
    },
  });

  const {
    formState: { isSubmitting },
  } = formContext;

  const handleSubmit = async (data: ExaminationCourseCreationFormInput) => {
    try {
      await onSubmit(data);
      logger.info('ExaminationCourseCreationForm: ', data);
    } catch (error) {
      const validationErrors = transformExamCourseCreationErrors(error);
      for (const validationError of validationErrors) {
        formContext.setError(validationError.property, {
          type: 'custom',
          message: validationError.message,
        });
      }
    }
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSubmit)}>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <FormTextField
            name='name'
            label='ชื่อหลักสูตรทดสอบ'
            placeholder='กรุณากรอกชื่อหลักสูตรทดสอบ'
            required
          />
          <FormTextField
            name='code'
            label='Exam Code'
            placeholder='กรุณากรอก Exam Code'
            helperText='กรอกได้สูงสุด 20 ตัวอักษร'
            required
          />
          <FormRadioSelection
            name='type'
            label='Course Type'
            options={[
              { label: 'Main', value: ExamCourseType.Main },
              { label: 'Mock', value: ExamCourseType.Mock },
            ]}
            row
            required
          />
          <FormRadioSelection
            name='language'
            label='Course Language'
            options={[
              { label: 'Thai', value: ExamCourseLanguage.Th },
              { label: 'English', value: ExamCourseLanguage.En },
            ]}
            row
            required
          />
          <FormSearchSelection
            name='group'
            label='Group'
            mode='fetch-debounce'
            placeholder='เลือกกลุ่ม/พิมพ์เพื่อสร้างกลุ่มใหม่'
            inputValue={inputValue ?? ''}
            options={group.options}
            loading={group.loading}
            addable
            onSearch={(inputValue) => {
              group.onSearch?.(inputValue);
            }}
            onChange={(_, option: GroupOption | null) => {
              setInputValue(option?.inputValue ?? option?.label ?? null);
            }}
            onInputChange={(_, value) => {
              setInputValue(value);
            }}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant='outlined' color='secondary' disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' disabled={isSubmitting} loading={isSubmitting}>
            Create Examination Course
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
